// composables/useGTag.ts

export default function useGTag() {

    // Configuration options for GTM and GA
    const gtmConfig = 'AW-941884461'; // Your GTM configuration ID
    const gaConfig = 'G-WQZLR3DYKD'; // Your GA configuration ID   
    const googleAdsConversionId = gtmConfig; // Your Google Ads conversion ID

    // Function to initialize GTM and GA
    const initGTag = (loadGTM: boolean, loadGA: boolean) => {

      console.log("initGTag trigger with loadGTM:", loadGTM, " and loadGA: ", loadGA);

      if(!loadGTM && !loadGA)
      {
        console.log("both GTag scripts should not be loaded. nothing to do here.");
        return;
      }
      // Check if the script is already loaded
      if (!document.querySelector("script[src='https://www.googletagmanager.com/gtag/js']")) {
        // Create the script tag
        const gtagScript = document.createElement('script');
        gtagScript.src = 'https://www.googletagmanager.com/gtag/js';
        gtagScript.async = true;
        
        // Error handler for the script
        gtagScript.onerror = function() {
          console.error("Failed to load gtag.js");
        }
  
        // Success handler for the script
        gtagScript.onload = function() {
          console.log("gtag.js script loaded successfully");
  
          // Initialize the dataLayer and gtag function
          window.dataLayer = window.dataLayer || [];
          window.gtag = function() { dataLayer.push(arguments); }
          gtag('js', new Date());
  
          // Configure GTM and Google Analytics based on provided flags
          if (loadGTM) {
            gtag('config', gtmConfig);
          }
          if (loadGA) {
            gtag('config', gaConfig);
          }
          trackUTMCampaign();
        }

        document.body.appendChild(gtagScript);
      }
    };

    const event = (action: string, params: Record<string, any> = {}) => {
         if (window.gtag)
        {
          console.log("gtag event", action, params);

          // For GTM - Event tracking
          window.gtag('event', action, params);

          // For Google Analytics 4 (GA4) - Event tracking
          // GA4 uses an event-based model and allows for a more flexible set of parameters
          window.gtag('event', action, {
              // Add parameters relevant for GA4 here
              ...params
          });
        }
    };

      // Abstracted Google Ads conversion tracking function
      const trackGoogleAdsConversion = (action, { value = 0, currency = 'EUR', transaction_id = '', conversionLabel = '' }) => {
        if (window.gtag) {
            window.gtag('event', 'conversion', {
                'send_to': `${googleAdsConversionId}/${conversionLabel}`,
                value,
                currency,
                'transaction_id': transaction_id
            });
        }
    };

    const trackUTMCampaign = () => {
      if (typeof window.gtag !== 'function') {
        console.warn("GTag is not initialized. Unable to track UTM campaigns.");
        return;
      }
  
      const params = new URLSearchParams(window.location.search);
      const utmSource = params.get('utm_source');
      const utmMedium = params.get('utm_medium');
      const utmCampaign = params.get('utm_campaign');
  
      if (utmSource && utmMedium && utmCampaign) {
        console.log("Sending UTM campaign event to GA4 and GTM:", utmSource, utmMedium, utmCampaign);
        trackCampaignEvent(utmSource, utmMedium, utmCampaign);
      }
    };

    const trackCampaignEvent = (source: string, medium: string, campaign: string) => {
      if (typeof window.gtag !== 'function') {
        console.warn("GTag is not initialized. Unable to send campaign events.");
        return;
      }

      event('campaign', {
        event_category: 'Marketing',
        event_label: 'Marketing Campaign',
        campaign_source: source,
        campaign_medium: medium,
        campaign_name: campaign
      });
    };
 
    const trackLogin = (): void => {
      console.log("gtag trackLogin");
      event('login');
    };

    const trackLogout = (): void => {
      console.log("gtag trackLogout");
      event('logout');
    };
  
    const trackSignUp = (): void => {
      console.log("gtag trackSignUp");
      event('sign_up');
      trackGoogleAdsConversion('sign_up', {
        conversionLabel: 'qrjvCLWpnJsZEK2IkMED',
        // Include any other parameters you deem necessary for the conversion tracking
        // For example, if you have a dynamic value or specific currency or transaction_id you want to track
      });
    };

    const trackPreferredCurrency = (currency: string): void => {
      console.log("gtag trackPreferredCurrency", currency);
      event('preferred_currency', { currency: currency });
    };
  
    const trackViewItem = (itemId: string, itemName: string, itemCategory: string): void => {
      console.log("gtag trackViewItem", itemId, itemName, itemCategory);
      event('view_item', { item_id: itemId, item_name: itemName, item_category: itemCategory });
    };
  
    const trackViewItemList = (categoryName: string, itemCount: number): void => {
      console.log("gtag trackViewItemList", categoryName, itemCount);
      event('view_item_list', { category_name: categoryName, item_count: itemCount });
    };
    
    const trackSearch = (searchTerm: string): void => {
      event('search', { search_term: searchTerm });
    };
  
    const trackAddToCart = (itemId: string, itemName: string, itemCategory: string, value: number): void => {    
      console.log("gtag trackAddToCart", itemId, itemName, itemCategory, value);
      event('add_to_cart', { item_id: itemId, item_name: itemName, item_category: itemCategory, value: value });

      // Use abstracted function for Google Ads conversion tracking
      trackGoogleAdsConversion('add_to_cart', {
        value,
        currency: 'EUR',
        transaction_id: itemId,
        conversionLabel: 'QQcRCOGnm5sZEK2IkMED' // This should be the specific conversion label for add-to-cart action
      });
    };
  
    const trackRemoveFromCart = (itemId: string): void => {
      console.log("gtag trackRemoveFromCart", itemId);
      event('remove_from_cart', { item_id: itemId });
    };
  
    const trackBeginCheckout = (): void => {
      console.log("gtag trackBeginCheckout");
      event('begin_checkout');
    };
  
    const trackPurchase = (value: number, currency: string, transactionId: string, items: any[]): void => {
      console.log("gtag trackPurchase", value, currency, transactionId, items);
      event('purchase', { value: value, currency: currency, transaction_id: transactionId, items: items });
    };
  
    const trackPurchaseCompleted = (transactionId: string): void => {
      console.log("gtag trackPurchaseCompleted", transactionId);
      event('purchase_completed', { transaction_id: transactionId });
    };
  
    const trackDownload = (itemName: string, itemType: string, version:string, licenseId: string): void => {
      console.log("gtag trackDownload", itemName, itemType, version, licenseId);
      event('download', { item_name: itemName, item_type: itemType, version: version, license_id: licenseId });
    };
  
    const trackTutorialBegin = (): void => {
      event('tutorial_begin');
    };
  
    const trackTutorialComplete = (): void => {
      event('tutorial_complete');
    };
  
    // For YouTube Video Events:
    const trackVideoPlay = (videoId: string): void => {
      event('video_play', { video_id: videoId });
    };
  
    const trackVideoPause = (videoId: string): void => {
      event('video_pause', { video_id: videoId });
    };
  
    const trackVideoEnd = (videoId: string): void => {
      event('video_end', { video_id: videoId });
    };

    return {
      initGTag,
      trackUTMCampaign,
      trackLogin,
      trackLogout,
      trackSignUp,
      trackPreferredCurrency,
      trackViewItem,
      trackViewItemList,
      trackSearch,
      trackAddToCart,
      trackRemoveFromCart,
      trackBeginCheckout,
      trackPurchase,
      trackPurchaseCompleted,
      trackDownload,
      trackTutorialBegin,
      trackTutorialComplete,
      trackVideoPlay,
      trackVideoPause,
      trackVideoEnd,
      trackCampaignEvent
    };
  }
  